const TAB_STORAGE_KEY = 'gowarehouse.tabId';

let memoryTabId = null;

const generateId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `tab-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const safeGetStorage = (storage) => {
  if (typeof window === 'undefined') return null;
  try {
    return window[storage];
  } catch {
    return null;
  }
};

export const getTabId = () => {
  const session = safeGetStorage('sessionStorage');
  if (!session) {
    if (!memoryTabId) {
      memoryTabId = generateId();
    }
    return memoryTabId;
  }

  let tabId = session.getItem(TAB_STORAGE_KEY);
  if (!tabId) {
    tabId = generateId();
    session.setItem(TAB_STORAGE_KEY, tabId);
  }

  return tabId;
};

const buildSelectionKey = (userId, tabId) => {
  if (!userId || !tabId) return null;
  return `merchantSelection:${userId}:${tabId}`;
};

export const getStoredMerchantSelection = (userId, tabId) => {
  const key = buildSelectionKey(userId, tabId);
  if (!key) return null;

  const storage = safeGetStorage('localStorage');
  if (!storage) return null;

  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && parsed.merchantId) {
      return parsed;
    }
  } catch (err) {
    console.warn('Failed to parse stored merchant selection', err);
  }

  return null;
};

export const setStoredMerchantSelection = (userId, tabId, merchantId) => {
  const key = buildSelectionKey(userId, tabId);
  if (!key || !merchantId) return;

  const storage = safeGetStorage('localStorage');
  if (!storage) return;

  try {
    storage.setItem(
      key,
      JSON.stringify({ merchantId, updatedAt: Date.now() })
    );
  } catch (err) {
    console.warn('Failed to store merchant selection', err);
  }
};

export const clearStoredMerchantSelection = (userId, tabId) => {
  const key = buildSelectionKey(userId, tabId);
  if (!key) return;

  const storage = safeGetStorage('localStorage');
  if (!storage) return;

  try {
    storage.removeItem(key);
  } catch (err) {
    console.warn('Failed to clear merchant selection', err);
  }
};

// 全域 merchant 選擇機制 - 基於 tab 焦點的最新 merchant
const FOCUS_MERCHANT_KEY = 'gowarehouse.focusMerchant';

export const getGlobalMerchantSelection = (userId) => {
  if (!userId) return null;

  const storage = safeGetStorage('localStorage');
  if (!storage) return null;

  try {
    const raw = storage.getItem(`${FOCUS_MERCHANT_KEY}:${userId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && parsed.merchantId) {
      return parsed;
    }
  } catch (err) {
    console.warn('Failed to parse focus merchant selection', err);
  }

  return null;
};

export const setFocusMerchantSelection = (userId, merchantId, tabId) => {
  if (!userId || !merchantId || !tabId) return;

  const storage = safeGetStorage('localStorage');
  if (!storage) return;

  try {
    const timestamp = Date.now();
    storage.setItem(
      `${FOCUS_MERCHANT_KEY}:${userId}`,
      JSON.stringify({ merchantId, tabId, updatedAt: timestamp })
    );
  } catch (err) {
    console.warn('Failed to store focus merchant selection', err);
  }
};

export const setGlobalMerchantSelection = () => {
  // 保持向後相容，但這個函數現在什麼都不做
  // 改用 setFocusMerchantSelection
  return;
};

export const getMostRecentMerchantSelection = (userId) => {
  if (!userId) return null;

  const storage = safeGetStorage('localStorage');
  if (!storage) return null;

  try {
    // 首先檢查全域選擇
    const globalMerchant = getGlobalMerchantSelection(userId);
    if (globalMerchant) return globalMerchant;

    // 如果沒有全域選擇，找最近更新的 tab 選擇
    const allKeys = Object.keys(storage);
    const merchantKeys = allKeys.filter(key => key.startsWith(`merchantSelection:${userId}:`));

    let mostRecent = null;
    let mostRecentTime = 0;

    merchantKeys.forEach(key => {
      try {
        const raw = storage.getItem(key);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.updatedAt > mostRecentTime) {
            mostRecentTime = parsed.updatedAt;
            mostRecent = parsed;
          }
        }
      } catch {
        // 忽略解析錯誤的項目
      }
    });

    return mostRecent;
  } catch (err) {
    console.warn('Failed to get most recent merchant selection', err);
    return null;
  }
};

// BroadcastChannel for cross-tab communication
let broadcastChannel = null;

const getBroadcastChannel = () => {
  if (typeof window === 'undefined' || !window.BroadcastChannel) return null;

  if (!broadcastChannel) {
    try {
      broadcastChannel = new BroadcastChannel('gowarehouse-merchant-sync');
    } catch (err) {
      console.warn('BroadcastChannel not available', err);
      return null;
    }
  }

  return broadcastChannel;
};

export const broadcastMerchantChange = (userId, merchantId) => {
  const channel = getBroadcastChannel();
  if (channel) {
    try {
      channel.postMessage({
        type: 'MERCHANT_CHANGED',
        userId,
        merchantId,
        timestamp: Date.now()
      });
    } catch (err) {
      console.warn('Failed to broadcast merchant change', err);
    }
  }
};

export const onMerchantChange = (callback) => {
  const channel = getBroadcastChannel();
  if (channel) {
    const handler = (event) => {
      if (event.data && event.data.type === 'MERCHANT_CHANGED') {
        callback(event.data);
      }
    };

    channel.addEventListener('message', handler);

    // 返回清理函數
    return () => {
      channel.removeEventListener('message', handler);
    };
  }

  return () => { }; // 空的清理函數
};
